import { createRef, type RefObject, useCallback, useEffect, useReducer, useRef } from 'react'

const useIntersectionObserver = (callback: IntersectionObserverCallback, options?: IntersectionObserverInit): IntersectionObserver => {
  // tracking observed elements in case of reconstruction
  const observedElements = useRef<Element[]>([])
  const refObserver = useRef<IntersectionObserver>(new IntersectionObserver(callback, options))

  useEffect(() => {
    const currentObserver = refObserver.current
    currentObserver.disconnect()
    const newObserver = new IntersectionObserver(callback, options)
    for (const observedElement of observedElements.current) {
      newObserver.observe(observedElement)
    }

    return () => {
      currentObserver.disconnect()
    }
  }, [callback, options])

  const observe = useCallback((target: Element) => {
    observedElements.current.push(target)
    refObserver.current.observe(target)
  }, [])

  const unobserve = useCallback((target: Element) => {
    const index = observedElements.current.indexOf(target)
    // nothing changed, so nothing was untracked
    if (index < 0) {
      return
    }
    refObserver.current.unobserve(target)
    observedElements.current = observedElements.current.splice(index, 1)
  }, [])

  const disconnect = useCallback(() => {
    observedElements.current = []
    refObserver.current.disconnect()
  }, [])

  return {
    root: refObserver.current.root,
    rootMargin: refObserver.current.rootMargin,
    thresholds: refObserver.current.thresholds,
    takeRecords: refObserver.current.takeRecords,
    observe: observe,
    unobserve: unobserve,
    disconnect: disconnect,
  }
}

interface Node<Element extends HTMLElement = HTMLElement> {
  node: RefObject<Element | null>
  lastEntry?: IntersectionObserverEntry
}

const isDomRectEqual = (a: DOMRectReadOnly | null, b: DOMRectReadOnly | null) => {
  return (
    a?.bottom === b?.bottom &&
    a?.height === b?.height &&
    a?.left === b?.left &&
    a?.right === b?.right &&
    a?.top === b?.top &&
    a?.width === b?.width &&
    a?.x === b?.x &&
    a?.y === b?.y
  )
}

/** Compares two IntersectionObserverEntries
 * **important:** does not check for time
 */
const isIntersectionEntryEqual = (a: IntersectionObserverEntry, b: IntersectionObserverEntry) => {
  if (!isDomRectEqual(a.boundingClientRect, b.boundingClientRect)) {
    return false
  }

  if (!isDomRectEqual(a.intersectionRect, b.intersectionRect)) {
    return false
  }
  if (!isDomRectEqual(a.rootBounds, b.rootBounds)) {
    return false
  }

  // ignoring explicitly a IntersectionObserverEntry.time
  return a.target === b.target && a.intersectionRatio === b.intersectionRatio && a.isIntersecting === b.isIntersecting
}

export const useIntersections = <ElementType extends HTMLElement = HTMLElement>(
  onChangedIntersections?: IntersectionObserverCallback,
  options?: IntersectionObserverInit,
) => {
  const elements = useRef<Node<ElementType>[]>([])

  // off-bounced useState to force re-rendering when intersections changed
  const [, forceUpdate] = useReducer((x) => x + 1, 0)

  const intersectionCallback = useCallback<IntersectionObserverCallback>(
    (entries, observer) => {
      const state = elements.current
      let changed = false
      for (const entry of entries) {
        const targetIndex = state.findIndex((x) => x?.node?.current === entry.target)
        const target = state[targetIndex]
        if (!target) {
          changed = true
          observer.unobserve(entry.target)
          continue
        }
        if (target.lastEntry === undefined || !isIntersectionEntryEqual(target.lastEntry, entry)) {
          changed = true
        }
        target.lastEntry = entry
      }
      if (changed) {
        if (!onChangedIntersections) {
          forceUpdate()
        } else {
          onChangedIntersections(entries, observer)
        }
      }
    },
    [onChangedIntersections],
  )
  const observer = useIntersectionObserver(intersectionCallback, options)

  const addRef = useCallback(
    (target: ElementType | null, index: number) => {
      // the index is, so far, untracked
      if (!elements.current[index]) {
        const ref: RefObject<ElementType | null> = createRef<ElementType | null>()
        ref.current = target
        elements.current[index] = { node: ref }
        if (target) {
          observer.observe(target)
        }
        return
      }

      // now the index is tracked, so run through the hoops
      const element = elements.current[index]
      // the target to be added is already observed
      if (element.node.current === target) {
        return
      }
      // the target is not observed, but there's already a node observed in its slot
      if (element.node.current) {
        observer.unobserve(element.node.current)
      }
      // if it is exists, overwrite and observe it
      if (target) {
        element.node.current = target
        observer.observe(target)
      } else if (element) {
        // that is somewhat unclean, but removing the lastEntry causes an update loop
        // deleting the element also causes an update loop, because the references expire
        // whenever they are re-rendered and cause the interaction observer to emit new
        // events.
        // element.lastEntry = undefined
        element.node.current = null
      }
    },
    [observer.observe, observer.unobserve],
  )

  const getEntries = useCallback(() => elements.current.filter((x) => !!x.node.current), [])

  return {
    addRef,
    getEntries,
  }
}
